/**
 * StageManager — owns the active stage and the extinction-event hook.
 *
 * A Stage is a pluggable chunk of gameplay. It gets init/update/cleanup and
 * a reference to shared context (world, renderer, data, save). New stages
 * slot into /src/stages/<id>/ and register here by id. Phase 1 ships the
 * "cell" stage only, but the switching mechanism is already in place.
 *
 * The `triggerExtinctionEvent` method is the Phase 2 prestige hook. For now
 * it emits `stage:complete` with a summary payload so the UI can show the
 * "Stage 1 Complete" screen. When prestige lands, it will additionally:
 *   - move earned DNA from run into meta
 *   - award a permanent upgrade point
 *   - reset run progress via saveManager.resetRunProgress()
 *   - advance to the next stage
 */

import type { Application, Container } from 'pixi.js';
import { bus } from './EventBus';
import type { World } from './ECS';
import type { GameData } from './AssetLoader';
import type { SaveManager } from './SaveManager';
import { saveManager } from './SaveManager';

export interface StageContext {
  app: Application;
  world: World;
  data: GameData;
  save: SaveManager;
  worldLayer: Container;
  fxLayer: Container;
  uiLayer: Container;
  screen: { width: number; height: number };
}

export interface Stage {
  readonly id: string;
  init(ctx: StageContext): void | Promise<void>;
  update(dt: number, elapsed: number): void;
  cleanup(): void;
  /** Optional hook — default implementation provided by stages via base class. */
  onResize?(width: number, height: number): void;
  /** Optional: pinch-zoom the camera by `factor` (>1 = zoom in). */
  zoomBy?(factor: number): void;
}

export class StageManager {
  private stages = new Map<string, () => Stage>();
  /** Sequence: which stage follows which on prestige (cell -> multicell -> ...). */
  private nextStage = new Map<string, string>();
  private current: Stage | null = null;
  private ctx: StageContext | null = null;
  private elapsed = 0;
  /** False while the current stage's init() is still awaiting (asset
   *  loads). update() must not tick a stage that hasn't finished init. */
  private ready = false;

  register(id: string, factory: () => Stage): void {
    this.stages.set(id, factory);
  }

  /** Define the prestige progression. Called once at boot in Game.ts. */
  setProgression(pairs: Array<[string, string]>): void {
    for (const [from, to] of pairs) this.nextStage.set(from, to);
  }

  /** Read the registered successor for a stage id, or null if it's terminal. */
  nextOf(id: string): string | null {
    return this.nextStage.get(id) ?? null;
  }

  async start(id: string, ctx: StageContext): Promise<void> {
    if (this.current) this.current.cleanup();
    const factory = this.stages.get(id);
    if (!factory) throw new Error(`No stage registered with id "${id}"`);
    this.ready = false;
    const stage = factory();
    this.current = stage;
    this.ctx = ctx;
    this.elapsed = 0;
    await stage.init(ctx);
    // A newer start() may have superseded this one while we awaited.
    if (this.current === stage) {
      this.ready = true;
      bus.emit('stage:started', { stageId: id });
    }
  }

  update(dt: number): void {
    if (!this.current || !this.ready) return;
    this.elapsed += dt;
    this.current.update(dt, this.elapsed);
    bus.emit('stage:tick', { dt, elapsed: this.elapsed });
  }

  resize(w: number, h: number): void {
    if (this.ctx) this.ctx.screen = { width: w, height: h };
    this.current?.onResize?.(w, h);
  }

  /**
   * Called when a stage's win condition fires. Emits `stage:complete` with the
   * summary payload. In Phase 2, wire prestige logic here BEFORE the emit.
   */
  triggerExtinctionEvent(summary: Record<string, unknown>): void {
    if (!this.current) return;
    bus.emit('stage:complete', { stageId: this.current.id, summary });
  }

  /**
   * Advance to the registered successor of the current stage. The
   * stage-complete UI calls this when the player clicks "Continue".
   * Run-progress is wiped (biomass, upgrades, mutations); meta survives,
   * including the carryover bonus saved by the previous stage's
   * triggerComplete. The new stage reads `meta.prestige.carryover` itself.
   */
  async advance(): Promise<boolean> {
    const fromId = this.current?.id;
    if (!fromId || !this.ctx) return false;
    const nextId = this.nextOf(fromId);
    if (!nextId || !this.stages.has(nextId)) {
      console.warn('[StageManager] advance() called but no successor for', fromId);
      return false;
    }
    saveManager.resetRunProgress(nextId);
    await this.start(nextId, this.ctx);
    return true;
  }

  currentId(): string | null { return this.current?.id ?? null; }
  currentStage(): Stage | null { return this.current; }
}

export const stageManager = new StageManager();
